// Authentication utility functions

const TOKEN_KEY = 'auth_token';

/**
 * Store JWT token in localStorage
 * @param token - JWT token to store
 */
export const storeToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Retrieve JWT token from localStorage
 * @returns JWT token or null if not found
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove JWT token from localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Parse JWT token to extract payload
 * @param token - JWT token to parse
 * @returns Parsed token payload or null
 */
export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param token - JWT token to check
 * @returns true if expired, false otherwise
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Failed to check token expiration:', error);
    return true;
  }
};

/**
 * Get token expiration date
 * @param token - JWT token
 * @returns Expiration date or null
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      return null;
    }

    // exp is in seconds, convert to milliseconds
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error('Failed to get token expiration date:', error);
    return null;
  }
};
